import { useMemo, useState } from "react";
import clsx from "clsx";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import type { OrdnerOut } from "@/lib/projects";

export function OrdnerPickerModal({
  open,
  onClose,
  ordner,
  loading,
  value,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  ordner: OrdnerOut[];
  loading: boolean;
  value: string;
  onSelect: (next: string) => void;
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ordner;
    return ordner.filter((o) =>
      [o.nummer, o.beschreibung, o.kunden_name]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q)),
    );
  }, [ordner, query]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Ordner auswählen"
      description="Alle Ordner der aktuellen Auswahl. Tipp: Nummer, Beschreibung oder Kunde."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Schließen
          </Button>
          {value ? (
            <Button
              variant="secondary"
              onClick={() => {
                onSelect("");
                onClose();
              }}
            >
              Auswahl löschen
            </Button>
          ) : null}
        </>
      }
    >
      <div className="space-y-3">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Suchen …"
          className="eh-input"
        />
        {loading ? (
          <div className="flex items-center justify-center py-8 text-label-secondary">
            <Spinner />
          </div>
        ) : filtered.length === 0 ? (
          <p className="py-8 text-center text-sm text-label-tertiary">Keine Ordner gefunden.</p>
        ) : (
          <ul className="max-h-[60vh] divide-y divide-separator overflow-auto rounded-ios border border-separator">
            {filtered.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  onClick={() => onSelect(o.nummer)}
                  className={clsx(
                    "flex w-full items-center justify-between gap-3 px-4 py-2 text-left transition",
                    value === o.nummer
                      ? "bg-accent-soft text-accent"
                      : "text-label hover:bg-fill-tertiary",
                  )}
                >
                  <span className="font-mono text-sm">{o.nummer}</span>
                  <span className="ml-3 truncate text-sm text-label-secondary">
                    {o.kunden_name ? `${o.kunden_name} · ` : ""}
                    {o.beschreibung}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
